import { z } from 'zod';
import { chartTypeIconSchema } from './chartTypeIcons';
import { isValidDataAppSlug } from './code';
import { dataAppVizPreviewSchema, getDataAppVizPreviewSchema, } from './preview';
import { dataAppVizSchema } from './types';
export const CHART_REGISTRY_INDEX_SCHEMA_VERSION = 1;
const SEMVER_VERSION_RE = /^\d+\.\d+\.\d+$/;
export const isSemverVersion = (value) => SEMVER_VERSION_RE.test(value);
/** Strict x.y.z numeric compare; throws on anything else. */
export const compareSemverVersions = (left, right) => {
    if (!isSemverVersion(left) || !isSemverVersion(right)) {
        throw new Error(`Cannot compare non-semver versions: ${left} vs ${right}`);
    }
    const l = left.split('.').map(Number);
    const r = right.split('.').map(Number);
    for (let i = 0; i < 3; i += 1) {
        if (l[i] !== r[i])
            return l[i] < r[i] ? -1 : 1;
    }
    return 0;
};
const semverString = z
    .string()
    .refine(isSemverVersion, { message: 'must be a strict x.y.z version' });
const registrySlug = z
    .string()
    .refine(isValidDataAppSlug, { message: 'must be a valid chart type slug' });
const registryArtifactSchema = z.object({
    path: z.string().min(1),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
});
const registryEntrySchema = z
    .object({
    slug: registrySlug,
    name: z.string().min(1),
    description: z.string(),
    version: semverString,
    publishedAt: z.string(),
    tags: z.array(z.string()).default([]),
    changelog: z.string().default(''),
    minLightdashVersion: semverString.nullable().default(null),
    channel: z.enum(['stable', 'beta']).optional(),
    vizSchema: dataAppVizSchema,
    preview: dataAppVizPreviewSchema.nullable().default(null),
    thumbnail: z.string().nullable().default(null),
    thumbnailDark: z.string().nullable().default(null),
    screenshots: z.array(z.string()).default([]),
    icon: chartTypeIconSchema.nullable().default(null),
    artifacts: z.object({
        source: registryArtifactSchema,
        dist: registryArtifactSchema,
    }),
})
    .superRefine((entry, ctx) => {
    if (!entry.preview)
        return;
    const result = getDataAppVizPreviewSchema(entry.vizSchema).safeParse(entry.preview);
    if (!result.success)
        result.error.issues.forEach((issue) => ctx.addIssue({ ...issue, path: ['preview', ...issue.path] }));
});
export const chartRegistryIndexSchema = z.object({
    schemaVersion: z.literal(CHART_REGISTRY_INDEX_SCHEMA_VERSION),
    generatedAt: z.string(),
    charts: z.array(registryEntrySchema),
});
const chartRegistryIndexEnvelopeSchema = z.object({
    schemaVersion: z.literal(CHART_REGISTRY_INDEX_SCHEMA_VERSION),
    generatedAt: z.string(),
    charts: z.array(z.unknown()),
});
/**
 * Parses a registry index, dropping entries that fail validation instead of
 * failing the whole index. The registry evolves independently of deployed
 * instances — one entry with, say, a channel value this build doesn't know
 * must hide that chart, not blank the entire library. An invalid envelope
 * (wrong schemaVersion, charts not an array) still throws: that's an
 * unusable registry, not a forward-compat entry.
 */
export const parseChartRegistryIndexTolerant = (raw) => {
    const envelope = chartRegistryIndexEnvelopeSchema.parse(raw);
    const charts = [];
    const dropped = [];
    for (const rawEntry of envelope.charts) {
        const parsed = registryEntrySchema.safeParse(rawEntry);
        if (parsed.success) {
            charts.push(parsed.data);
        }
        else {
            const slug = typeof rawEntry === 'object' &&
                rawEntry !== null &&
                'slug' in rawEntry &&
                typeof rawEntry.slug === 'string'
                ? rawEntry.slug
                : null;
            dropped.push({
                slug,
                message: parsed.error.issues
                    .map((issue) => `${issue.path.join('.')}: ${issue.message}`)
                    .join('; '),
            });
        }
    }
    return {
        index: {
            schemaVersion: envelope.schemaVersion,
            generatedAt: envelope.generatedAt,
            charts,
        },
        dropped,
    };
};
// Manifest `registry` block written by a registry-installed download.
export const dataAppManifestRegistryProvenanceSchema = z.object({
    slug: registrySlug,
    version: semverString,
    url: z.string().min(1),
});
const registryFileRef = z.object({ fileName: z.string().min(1) });
// Shape of the per-version `charts/<slug>/<version>/entry.json` the registry
// publishes: images are file names and artifacts carry digests only.
const chartRegistryVersionSnapshotSchema = z.object({
    slug: registrySlug,
    name: z.string().min(1),
    description: z.string(),
    version: semverString,
    publishedAt: z.string(),
    tags: z.array(z.string()).default([]),
    changelog: z.string().default(''),
    minLightdashVersion: semverString.nullable().default(null),
    channel: z.enum(['stable', 'beta']).optional(),
    vizSchema: dataAppVizSchema,
    preview: dataAppVizPreviewSchema.nullable().default(null),
    screenshots: z.array(registryFileRef).default([]),
    thumbnailDark: registryFileRef.nullable().optional(),
    icon: chartTypeIconSchema.nullable().default(null),
    artifacts: z.object({
        source: z.object({ sha256: z.string().regex(/^[a-f0-9]{64}$/) }),
        dist: z.object({ sha256: z.string().regex(/^[a-f0-9]{64}$/) }),
    }),
});
/**
 * Converts a per-version registry snapshot into a full entry with paths
 * rooted at `charts/<slug>/<version>/`. The snapshot's own identity must
 * match the requested one so a misrouted file never installs as another chart.
 */
export const parseChartRegistryVersionSnapshot = (raw, expected) => {
    const snapshot = chartRegistryVersionSnapshotSchema.parse(raw);
    if (snapshot.slug !== expected.slug) {
        throw new Error(`Chart registry snapshot is for "${snapshot.slug}", expected "${expected.slug}"`);
    }
    if (snapshot.version !== expected.version) {
        throw new Error(`Chart registry snapshot is version ${snapshot.version}, expected ${expected.version}`);
    }
    const prefix = `charts/${expected.slug}/${expected.version}`;
    const screenshots = snapshot.screenshots.map((s) => `${prefix}/${s.fileName}`);
    const { channel, ...rest } = snapshot;
    return registryEntrySchema.parse({
        ...rest,
        ...(channel !== undefined ? { channel } : {}),
        thumbnail: screenshots[0] ?? null,
        thumbnailDark: snapshot.thumbnailDark
            ? `${prefix}/${snapshot.thumbnailDark.fileName}`
            : null,
        screenshots,
        artifacts: {
            source: {
                path: `${prefix}/source.tar`,
                sha256: snapshot.artifacts.source.sha256,
            },
            dist: {
                path: `${prefix}/dist.tar`,
                sha256: snapshot.artifacts.dist.sha256,
            },
        },
    });
};
const chartRegistryIndexSchemaMatchesApiType = true;
void chartRegistryIndexSchemaMatchesApiType;
//# sourceMappingURL=registry.js.map